import React, { useContext, useState } from 'react'
import { SiteModeContext } from '../../../components/mainFrame/MainFrame'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import PersonIcon from '@mui/icons-material/Person'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import SvgIcon from '@mui/material/SvgIcon'
import EmergencyShareIcon from '@mui/icons-material/EmergencyShare'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import VisibilityIcon from '@mui/icons-material/Visibility'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import VaccinesIcon from '@mui/icons-material/Vaccines'
import NextWeekIcon from '@mui/icons-material/NextWeek'
import ReplyIcon from '@mui/icons-material/Reply'
import FindInPageIcon from '@mui/icons-material/FindInPage'
import ShareIcon from '@mui/icons-material/Share'
import QuizIcon from '@mui/icons-material/Quiz'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import { useNavigate } from 'react-router-dom'
import { EPCRContext } from './EPCR'
import DeleteIcon from '@mui/icons-material/Delete'
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle'
import DeletePopup from './DeletePopup'
import {
  AttachFile,
  CloudDone,
  CloudDownload,
  CloudOff,
  CloudSync,
  CloudUpload,
} from '@mui/icons-material'
import { UserContext } from '../../../App'
import {
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { syncStatusColors } from './EPCRHeader'
import BoltIcon from '@mui/icons-material/Bolt'
import FavoriteIcon from '@mui/icons-material/Favorite'
import Badge from '@mui/material/Badge'
import { listPendingDetails } from './liveCapture/engine'
import { getFormType } from './formTypes'

// Per-section "has content" checks for the progress dots in the nav.
// Deliberately conservative - only fields a clinician actually filled in
// count (the new-record defaults in EPCR.jsx must not light anything up).
const SECTION_DONE = {
  Demographics: (v) =>
    !!(
      v?.demographics?.firstName ||
      v?.demographics?.lastName ||
      v?.demographics?.dob ||
      v?.demographics?.lastNameUnknown ||
      v?.demographics?.dobUnknown
    ),
  PMHx: (v) =>
    ['diseases', 'allergies', 'medicines'].some(
      (key) => v?.pmhx?.[key]?.length,
    ) || !!(v?.pmhx?.socialHx || v?.pmhx?.familyHx),
  History: (v) => !!(v?.history?.presentingComplaint || v?.history?.freeText),
  Assess: (v) => !!v?.assess?.modules?.length,
  Vitals: (v) => !!v?.vitals?.obs?.length,
  Treatment: (v) => !!v?.treatment?.modules?.length,
  Attachments: (v) => !!v?.attachments?.length,
  Plan: (v) =>
    !!(v?.plan?.ptOutcome || v?.plan?.planNotes || v?.plan?.adviceGiven),
  Review: (v) => v?.review?.status === 'Submitted',
}

const SidebarEPCR = ({ selectedEPCRModule, setSelectedEPCRModule, onExit }) => {
  const navigate = useNavigate()
  const { sidebarState, setSidebarState, setSiteMode, setEpcrPracticeMode } =
    useContext(SiteModeContext)
  const {
    setOpenDelete,
    formValues,
    isDraftDirty,
    isSaving,
    uploadState,
    downloadState,
    setFormTypeDialogOpen,
  } = useContext(EPCRContext)
  const { mobileOpen, collapse } = sidebarState
  const { user } = useContext(UserContext)
  const [showDirtyDraftWarning, setShowDirtyDraftWarning] = useState(false)
  const theme = useTheme()
  const showMobileSidebar = useMediaQuery(theme.breakpoints.down('lg'))

  const isUploading = uploadState === 'pending'
  const isDownloading = downloadState === 'pending'
  const hasSyncError = uploadState === 'error' || downloadState === 'error'
  const isFullySynced = uploadState === 'done' && downloadState === 'done'
  const hasTreatmentLocationOwner =
    !!formValues?.review?.currentTreatmentLocation

  const deleteIsAllowed =
    !formValues?.demographics?.firstName &&
    !formValues?.demographics?.lastName &&
    !formValues?.demographics?.lastNameUnknown &&
    !formValues?.demographics?.dob &&
    !formValues?.demographics?.dobUnknown &&
    // Unidentified HEMS patients still have a clinical record if anything
    // was logged on a live console
    !formValues?.cardiacArrest?.events?.length &&
    !formValues?.criticalCare?.events?.length

  const selectModule = (title) => {
    setSelectedEPCRModule(title)
    setSidebarState((prev) => ({ ...prev, mobileOpen: false }))
  }

  const exitRecord = () => {
    if (isDraftDirty || isSaving) {
      setShowDirtyDraftWarning(true)
    } else if (onExit) {
      setSelectedEPCRModule('')
      onExit()
    } else {
      setSiteMode('normal')
      setEpcrPracticeMode(false)
      setSelectedEPCRModule('')
      navigate('/')
    }
  }

  const isCriticalCare = formValues?.mode === 'Critical Care'
  const hasArrest = !!formValues?.cardiacArrest?.startedAt
  const pendingCount =
    isCriticalCare || hasArrest ? listPendingDetails(formValues).length : 0

  const PanelButton = ({ icon, title, label, badge, color }) => {
    const isSelected = title === selectedEPCRModule
    const done = SECTION_DONE[title]?.(formValues)
    return (
      <Box
        onMouseDownCapture={(event) => {
          event.preventDefault()
          event.stopPropagation()
          selectModule(title)
        }}
        onClick={() => selectModule(title)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          mx: 1,
          px: 1.25,
          py: 0.75,
          borderRadius: 2,
          cursor: 'pointer',
          color: isSelected ? '#fff' : 'rgba(226,232,240,0.72)',
          backgroundColor: isSelected
            ? 'rgba(255,255,255,0.12)'
            : 'transparent',
          boxShadow: isSelected ? 'inset 3px 0 0 0 #2DD4BF' : 'none',
          transition: 'background-color 0.15s ease, color 0.15s ease',
          '&:hover': {
            backgroundColor: isSelected
              ? 'rgba(255,255,255,0.12)'
              : 'rgba(255,255,255,0.06)',
            color: '#fff',
          },
        }}
      >
        <Badge badgeContent={badge} color="warning" invisible={!badge}>
          <SvgIcon
            component={icon}
            sx={{ fontSize: 18, color: color ?? 'inherit' }}
          />
        </Badge>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: isSelected ? 700 : 500,
            lineHeight: 1.2,
            flex: 1,
            color: 'inherit',
          }}
        >
          {label ?? title}
        </Typography>
        {done && (
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#2DD4BF',
              flexShrink: 0,
            }}
          />
        )}
      </Box>
    )
  }

  const GroupLabel = ({ children }) => (
    <Typography
      sx={{
        px: 2.25,
        pt: 1.5,
        pb: 0.5,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        color: 'rgba(148,163,184,0.75)',
      }}
    >
      {children}
    </Typography>
  )

  const formType = getFormType(formValues?.mode)

  const syncLabel = hasSyncError
    ? 'Not synced'
    : isUploading || isDownloading || isSaving
      ? 'Syncing…'
      : 'Saved'
  const syncColor = hasSyncError
    ? '#F87171'
    : isUploading || isDownloading || isSaving
      ? '#FBBF24'
      : '#2DD4BF'

  const SideBarComponent = () => (
    <Box
      sx={{
        width: '100%',
        minWidth: 200,
        background: 'linear-gradient(180deg, #192432 0%, #0F172A 100%)',
        zIndex: 1,
        height: '100%',
        maxHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Button
        startIcon={<ReplyIcon sx={{ fontSize: 16 }} />}
        onMouseDownCapture={(event) => {
          event.preventDefault()
          event.stopPropagation()
          exitRecord()
        }}
        onClick={exitRecord}
        fullWidth
        sx={{
          justifyContent: 'flex-start',
          px: 2,
          py: 1,
          borderRadius: 0,
          color: 'rgba(226,232,240,0.85)',
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
        }}
      >
        Exit record
      </Button>
      <Box
        onClick={() => setFormTypeDialogOpen(true)}
        sx={{
          mx: 1,
          mb: 0.5,
          p: 1.25,
          borderRadius: 2,
          backgroundColor: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.08)',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
        }}
      >
        <Stack direction="row" alignItems="center" gap={0.75}>
          <SvgIcon
            component={formType.icon}
            sx={{ fontSize: 16, color: '#2DD4BF' }}
          />
          <Typography
            sx={{ fontSize: 12.5, fontWeight: 700, color: '#fff', flex: 1 }}
          >
            {formType.label}
          </Typography>
          <ChangeCircleIcon
            sx={{ fontSize: 15, color: 'rgba(148,163,184,0.9)' }}
          />
        </Stack>
        {formValues?.epcrNumber && (
          <Typography
            sx={{
              mt: 0.25,
              fontSize: 11,
              fontFamily: 'ui-monospace, monospace',
              letterSpacing: '0.06em',
              color: 'rgba(148,163,184,1)',
            }}
          >
            Record {formValues.epcrNumber}
          </Typography>
        )}
      </Box>
      <Stack width="100%" spacing={0.25} minHeight={0} flex={1} overflow="auto" pb={1}>
        {(isCriticalCare || hasArrest) && (
          <>
            <GroupLabel>Live</GroupLabel>
            {isCriticalCare && (
              <PanelButton
                icon={BoltIcon}
                title="Live"
                label="Live console"
                badge={pendingCount}
              />
            )}
            {hasArrest && (
              <PanelButton
                icon={FavoriteIcon}
                title="Arrest"
                label="Cardiac arrest"
                badge={isCriticalCare ? 0 : pendingCount}
                color="#FF8A80"
              />
            )}
          </>
        )}
        <GroupLabel>Patient</GroupLabel>
        <PanelButton icon={EmergencyShareIcon} title="Incident" />
        <PanelButton icon={PersonIcon} title="Demographics" />
        <PanelButton icon={MedicalServicesIcon} title="PMHx" />
        <PanelButton icon={QuizIcon} title="History" />
        <GroupLabel>Clinical</GroupLabel>
        <PanelButton icon={VisibilityIcon} title="Assess" />
        <PanelButton icon={MonitorHeartIcon} title="Vitals" />
        <PanelButton icon={VaccinesIcon} title="Treatment" />
        <PanelButton icon={AttachFile} title="Attachments" />
        <GroupLabel>Complete</GroupLabel>
        <PanelButton icon={NextWeekIcon} title="Plan" />
        {/* <PanelButton icon={AmbulanceIcon} title="Transport" /> */}
        <PanelButton icon={ShareIcon} title="Sharing" />
        <PanelButton icon={FindInPageIcon} title="Review" />
      </Stack>
      <Box
        sx={{
          p: 1.25,
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        {(formValues?.review?.completedBy?._id === user?._id ||
          hasTreatmentLocationOwner) &&
          deleteIsAllowed && (
            <Button
              size="small"
              fullWidth
              startIcon={<DeleteIcon sx={{ fontSize: 15 }} />}
              onClick={() => setOpenDelete(true)}
              sx={{
                mb: 0.75,
                justifyContent: 'flex-start',
                textTransform: 'none',
                color: 'rgba(248,113,113,0.9)',
                '&:hover': { backgroundColor: 'rgba(248,113,113,0.1)' },
              }}
            >
              Delete record
            </Button>
          )}
        <Stack direction="row" alignItems="center" gap={0.75} px={0.5}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: syncColor,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{ fontSize: 11.5, color: 'rgba(203,213,225,0.85)' }}
          >
            {syncLabel}
          </Typography>
        </Stack>
      </Box>
    </Box>
  )
  const toggleDrawer = (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return
    }
    setSidebarState((prev) => ({ ...prev, mobileOpen: false }))
  }

  return (
    <>
      <Box
        sx={{
          display: {
            xs: 'none',
            lg: 'flex',
          },
          width: '200px',
          flexShrink: 0,
        }}
      >
        {SideBarComponent()}
      </Box>
      {showMobileSidebar && (
        <Box
          sx={{
            display: {
              xs: 'flex',
              lg: 'none',
            },
          }}
        >
          <Drawer
            anchor="left"
            open={mobileOpen}
            onClose={toggleDrawer}
            sx={{
              display: {
                xs: 'flex',
                lg: 'none',
              },
            }}
          >
            {SideBarComponent()}
          </Drawer>
        </Box>
      )}
      <Dialog
        open={showDirtyDraftWarning}
        onClose={() => {
          if (isFullySynced) setShowDirtyDraftWarning(false)
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, overflow: 'hidden' },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {isFullySynced ? (
              <CloudDone color="success" />
            ) : hasSyncError ? (
              <CloudOff color="error" />
            ) : (
              <CloudSync color="warning" />
            )}

            <Typography variant="h6" fontWeight={700}>
              {isFullySynced
                ? 'ePCR Synced'
                : hasSyncError
                  ? 'Sync Problem'
                  : 'Syncing ePCR'}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            {!isFullySynced && !hasSyncError && <LinearProgress />}

            <Alert
              severity={
                isFullySynced ? 'success' : hasSyncError ? 'error' : 'warning'
              }
              sx={{ borderRadius: 3 }}
            >
              {isFullySynced
                ? 'This ePCR has been synced successfully. You can safely continue.'
                : hasSyncError
                  ? 'This ePCR could not be synced. Please check your connection before leaving.'
                  : 'This ePCR is still syncing. Please wait before leaving this page.'}
            </Alert>
            <Stack direction="row" gap={1} justifyContent="center">
              <CloudUpload
                sx={{
                  color: () => syncStatusColors(uploadState),
                  width: '4rem',
                  height: '4rem',
                }}
              />
              <CloudDownload
                sx={{
                  color: () => syncStatusColors(downloadState),
                  width: '4rem',
                  height: '4rem',
                }}
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          {!isFullySynced && (
            <Button
              onClick={() => setShowDirtyDraftWarning(false)}
              variant="outlined"
            >
              Stay on ePCR
            </Button>
          )}

          {isFullySynced && (
            <Button
              onClick={() => {
                setShowDirtyDraftWarning(false)
                setSelectedEPCRModule('')
                if (onExit) {
                  onExit()
                } else {
                  setSiteMode('normal')
                  setEpcrPracticeMode(false)
                  navigate('/')
                }
              }}
              variant="contained"
              color="success"
            >
              Continue
            </Button>
          )}

          {hasSyncError && (
            <Button
              color="warning"
              variant="contained"
              onClick={() => {
                // allow leaving but keep local draft
              }}
            >
              Leave & Keep Draft
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default SidebarEPCR
